import { ToolButton } from '@rcs/ui';
import { useEffect } from 'react';
import { messages } from '../i18n/ja';

/** メニューを出す位置（画面の座標）と、対象の制御点。 */
export interface ContextMenuTarget {
  readonly id: string;
  readonly x: number;
  readonly y: number;
}

const MENU_WIDTH = 220;
const MENU_HEIGHT = 3 * 48 + 16;

/**
 * 制御点の長押しメニュー（iPad のタッチ操作：追加・削除）。画面の外へ出ないように位置を収める。
 * Esc または外側のタップで閉じる。ボタンは 44px 以上。
 */
export function ContextMenu({
  target,
  canAdd,
  canRemove,
  onAddAfter,
  onRemove,
  onClose,
}: {
  readonly target: ContextMenuTarget;
  readonly canAdd: boolean;
  readonly canRemove: boolean;
  readonly onAddAfter: () => void;
  readonly onRemove: () => void;
  readonly onClose: () => void;
}) {
  const t = messages.contextMenu;
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);
  const left = Math.max(8, Math.min(target.x, window.innerWidth - MENU_WIDTH - 8));
  const top = Math.max(8, Math.min(target.y, window.innerHeight - MENU_HEIGHT - 8));
  return (
    <>
      <div
        className="context-menu__backdrop"
        data-testid="context-menu-backdrop"
        onPointerDown={onClose}
      />
      <div
        className="context-menu"
        role="menu"
        aria-label={t.label}
        data-testid="context-menu"
        style={{ left, top, width: MENU_WIDTH }}
      >
        <ToolButton
          label={t.addAfter}
          disabled={!canAdd}
          onClick={() => {
            onAddAfter();
            onClose();
          }}
          data-testid="context-menu-add"
        />
        <ToolButton
          label={t.remove}
          disabled={!canRemove}
          onClick={() => {
            onRemove();
            onClose();
          }}
          data-testid="context-menu-remove"
        />
        <ToolButton label={t.close} onClick={onClose} data-testid="context-menu-close" />
      </div>
    </>
  );
}
